import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useCallback, useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import InfiniteScroll from "react-swr-infinite-scroll";

import Bugsnag from "@bugsnag/js";
import classNames from "classnames";
import { uniqBy } from "lodash";
import truncate from "lodash/truncate";
import { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import AccountUpgradeModal from "components/AccountUpgradeModal";
import { Loader } from "components/customElements/loader";
import DianomiAd from "components/DianomiAd";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import FocusedSwitch from "components/FocusedSwitch";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import Paginator from "components/Paginator";
import PublicationSnippet from "components/PublicationSnippet";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import ViewModeToggle from "components/ViewModeToggle";
import useActivityTracker from "hooks/useActivityTracker";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher, fetchApi } from "utils/baseFetcher";

import styles from "./style.module.scss";

type EntityPageInsightsListProps = {
  slug?: string;
};

export const entityInsightsUrl = (
  slug: string,
  pageIndex: number,
  filterTxt?: string,
  sortStr?: string,
  mobile?: boolean,
  tab?: string,
  pageSize?: string,
  focused?: boolean
) => {
  const url = new ApiEndpoint(
    `/api/v2/entities/${slug}/${
      focused ? "primary-insights" : "aggregated-insights"
    }`
  );
  url.searchParams.append(
    "include",
    "content-vertical,account,company,primary-entity,latest-comment-thread,latest-comment-thread.commenter,latest-comment-thread.attachment,smart-summary"
  );
  url.searchParams.append("filter[aasm-state]", "published");
  switch (tab) {
    case "insights":
      url.searchParams.append("filter[is-broker]", "false");
      url.searchParams.append("filter[is-primer]", "false");
      url.searchParams.append("filter[is-issuerpaid]", "false");
      url.searchParams.append("filter[is-exchange-sponsored]", "false");
      break;
    case "broker-reports":
      url.searchParams.append("filter[is-broker]", "true");
      break;
    case "sponsored":
      url.searchParams.append("filter[or][is-issuerpaid]", "true");
      url.searchParams.append("filter[or][is-exchange-sponsored]", "true");
      break;
    case "syndicated":
      url.searchParams.append("filter[is-syndicated]", "true");
      break;
    case "podcast":
      url.searchParams.append("filter[is-podcast]", "true");
      break;
    case "buyside":
      url.searchParams.append("filter[is-buyside]", "true");
      break;
    case "primer":
      url.searchParams.append("filter[is-primer]", "true");
      break;
  }
  url.searchParams.append("page[size]", pageSize || "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append(
    "sort",
    sortStr === "-score,-published-at" && focused
      ? "-published-at"
      : sortStr || (focused ? "-published-at" : "-score,-published-at")
  );
  url.searchParams.append(
    "fields[insights]",
    "content-vertical,account,company,primary-entity,latest-comment-thread,smart-summary" +
      ",slug,tagline,shareable-links,tagline," +
      (mobile ? "" : "byline,executive-summary,boomerang-reason,") +
      "total-combined-read,is-exchange-sponsored,is-broker,is-issuerpaid,is-commissioned,is-primer,is-boost,is-buyside,is-syndicated,is-podcast," +
      "imperative,published-at,republished-at,conviction,is-embargoed,total-discussions"
  );
  url.searchParams.append("fields[entities]", "slug,pretty-name");
  url.searchParams.append(
    "fields[accounts]",
    "slug,name,short-name,avatar-url,title,company-name,summary,areas-of-expertise,is-followed,is-insight-provider,activated"
  );
  url.searchParams.append(
    "fields[companies]",
    "slug,name,avatar-url,publish-under-company-name,title,analysts-count,summary,areas-of-expertise,is-followed"
  );
  url.searchParams.append("fields[content-verticals]", "slug,name");

  if (filterTxt) {
    url.searchParams.append("filter[fulltext]", filterTxt);
  }

  return url.href;
};

export const sortOptions = [
  { id: "relevant", name: "Suggested", sortStr: "-score,-published-at" },
  { id: "recent", name: "Recent", sortStr: "-published-at,-updated-at" },
  {
    id: "viewed",
    name: "Viewed",
    sortStr: "-total-combined-read,-published-at",
  },
  {
    id: "discussed",
    name: "Discussed",
    sortStr: "-total-discussions,-published-at",
  },
];

const EntityPageInsightsList = ({
  slug: slugProp,
}: EntityPageInsightsListProps) => {
  const router = useRouter();
  const slug = slugProp || (router.query.slug as string);
  const tab = router.query.tab as string;
  const page = router.query.page && parseInt(router.query.page as string);

  const { mobile } = useMediaQuery();
  const { setNotification } = useNotification();

  const { authenticated, account } = useAuthentication();

  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  const [focusedOnly, setFocusedOnly] = useState(false);
  const [isCompact, setCompactView] = useState(mobile);
  const [sortOption, setSortOption] = useState(sortOptions[0]);
  const [hoverPrimer, setHoverPrimer] = useState(false);

  const { data: entity } = useApi(entityUrl(slug));
  const { toggleModal } = useSignInSignUp();
  const [showStripeModal, toggleStripeModal] = useToggle();
  const { trackActivity } = useActivityTracker();

  const swr = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;

      return entityInsightsUrl(
        slug,
        page || pageIndex + 1,
        filterTxt,
        sortOption.sortStr,
        mobile,
        tab,
        authenticated && !page ? "12" : "5",
        focusedOnly
      );
    },
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
    }
  );
  const { data, isValidating, mutate, size, setSize } = swr;

  const totalPages = data?.[size - 1]?.meta?.totalPages;
  const hasMore = !page && totalPages > size;
  const isEmpty = !data?.[0]?.data?.length;
  const isRefreshing = isValidating && data && data.length === size;

  const publications = useMemo(() => {
    const items = swr.data?.reduce((acc, page) => {
      if (page?.data) return [...acc, ...page.data];
      return acc;
    }, []);

    return uniqBy(items, (publication: Insight) => publication.id);
  }, [swr.data]);

  const origin = useMemo(() => {
    const endpoint = `entities/${slug}/aggregated-insights`;
    let sort = sortOption || sortOptions?.[0];
    const filters: { [key: string]: string } = {};

    filters["include"] =
      "content-vertical,account,company,primary-entity,latest-comment-thread,latest-comment-thread.commenter";

    filters["fields[insights]"] =
      "content-vertical,account,company,primary-entity,latest-comment-thread" +
      ",slug,tagline,shareable-links,tagline," +
      (mobile ? "" : "byline,executive-summary,boomerang-reason,") +
      "total-combined-read,is-exchange-sponsored,is-broker,is-issuerpaid,is-commissioned,is-boost,is-buyside,is-syndicated,is-podcast," +
      "imperative,published-at,republished-at,conviction,is-embargoed,total-discussions";
    filters["fields[entities]"] = "slug,pretty-name";
    filters["fields[accounts]"] =
      "slug,name,short-name,avatar-url,title,company-name,is-followed,is-insight-provider,activated";
    filters["fields[companies]"] =
      "slug,name,avatar-url,publish-under-company-name,title,analysts-count,is-followed";
    filters["fields[content-verticals]"] = "slug,name";
    switch (tab) {
      case "insights":
        filters["filter[is-broker]"] = "false";
        filters["filter[is-issuerpaid]"] = "false";
        filters["filter[is-exchange-sponsored]"] = "false";
        break;
      case "broker-reports":
        filters["filter[is-broker]"] = "true";
        break;
      case "sponsored":
        filters["filter[or][is-issuerpaid]"] = "true";
        filters["filter[or][is-exchange-sponsored]"] = "true";
        break;
    }

    if (filterTxt) {
      filters["filter[fulltext]"] = filterTxt;
    }

    return {
      endpoint,
      sort,
      filters,
      page: size,
      pageSize: 5,
    };
  }, [filterTxt, mobile, size, slug, sortOption, tab]);

  const onRequestPrimer = useCallback(async () => {
    if (!entity?.id) {
      setNotification("Unable to create request", "error");
    }

    if (!account?.canRequestAIPrimer) {
      if (!authenticated) {
        toggleModal("sign-up");
      } else {
        toggleStripeModal();
      }
      return;
    }

    try {
      const response = await fetchApi("/api/v3/request-insight-ai-primer", {
        method: "POST",
        body: JSON.stringify({
          "entity-id": entity?.id,
        }),
      });

      if (response.errors) {
        if (response?.errors?.[0].title === "Record conflict") {
          setNotification(
            `A primer for ${entity?.prettyName} was recently published. New primers cannot be created at this time.`,
            "error"
          );
        } else if (
          response?.errors?.[0].title === "Request is already being processed"
        ) {
          setNotification(
            `Primer report is already being generated for ${entity?.prettyName}`,
            "error"
          );
        } else {
          setNotification(
            `Unable to create request${
              response?.errors?.[0].title
                ? `, ${response?.errors?.[0].title}`
                : ""
            }`,
            "error"
          );
        }
        return;
      }

      trackActivity("aiprimer.request", entity);

      setNotification(
        "We are generating your primer (this may take up to 2 minutes). You’ll receive a notification once it’s ready. Feel free to keep browsing in the meantime.",
        "success"
      );
    } catch (error) {
      setNotification("Unable to create request", "error");
    }
  }, [
    account?.canRequestAIPrimer,
    authenticated,
    entity,
    setNotification,
    toggleModal,
    toggleStripeModal,
    trackActivity,
  ]);

  const onEmptyStateActionClick = useCallback(async () => {
    router.push(`/entities/${slug}/research`);
  }, [router, slug]);

  useEffect(() => {
    if (filterTxtValue?.length >= 3) {
      setFilterTxt(filterTxtValue);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [filterTxtValue]);

  useEffect(() => {
    if (!hasMore && !page) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      sessionStorage.removeItem("entity-slug");
    } catch (error: any) {
      Bugsnag.notify(error);
    }
  }, [slug]);

  useEffect(() => {
    if (page && totalPages && page > totalPages) {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          page: totalPages,
        },
      });
    }
  }, [page, router, totalPages]);

  const { prettyName: name } = entity || {};
  const firstPublication = data?.[0]?.data?.[0];
  const description = truncate(
    `Independent Research, Discussions, Filings, Analytics, SmartScore, Quant Rating and Latest News on ${name} (${
      entity?.uniqueTicker
    })${firstPublication ? ` - ${firstPublication?.tagline}` : "."}`,
    {
      length: 155,
      separator: /[/\- ]/,
    }
  );

  const snippetStyle = !mobile ? "snippetV2" : "snippet";

  const controller = useMemo(() => {
    if (tab === "primer") {
      return (
        <div
          className={twMerge(
            styles.filter,
            "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center md:justify-between",
            "overflow-auto"
          )}
        >
          <div className="flex items-center">
            <span
              className={twMerge(styles.refresh, "hidden md:block")}
              onClick={() => mutate()}
            >
              Refresh
            </span>
            <div
              className={twMerge(
                styles.filterBox,
                "ml-0 md:ml-[30px] mr-[10px]"
              )}
            >
              <FontAwesomeIcon icon={faSearch} />
              <OverlayTrigger
                placement="bottom"
                overlay={
                  filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
                }
              >
                <input
                  value={filterTxtValue}
                  autoComplete="false"
                  autoCapitalize="false"
                  onChange={(e) => setFilterTxtValue(e.target.value)}
                  aria-label="search"
                  placeholder="Search"
                  className="min-w-[200px]"
                />
              </OverlayTrigger>
            </div>
          </div>
          <div className="flex items-center md:w-auto space-x-[10px]">
            {!isValidating && (
              <OverlayTrigger
                placement={mobile ? "bottom" : "top"}
                overlay={
                  <Tooltip>
                    <p>Create a concise company primer using Smartkarma AI</p>
                  </Tooltip>
                }
              >
                <div
                  className={twMerge(
                    "cursor-pointer min-w-[150px]",
                    "flex items-center justify-center",
                    "p-[7px] px-[16px] text-[12px] rounded-full",
                    "gap-[5px] bg-white hover:bg-ink-pink text-white",
                    "border-1 border-solid border-ink-pink text-ink-pink hover:text-white",
                    !account?.canRequestAIPrimer &&
                      "border-inactive-gray hover:bg-inactive-gray text-inactive-gray"
                  )}
                  onClick={onRequestPrimer}
                  onMouseEnter={() => setHoverPrimer(true)}
                  onMouseLeave={() => setHoverPrimer(false)}
                >
                  <Image
                    src={
                      hoverPrimer
                        ? `https://assets.smartkarma.com/icons/sparkle-single/sparkle-white.svg`
                        : !account?.canRequestAIPrimer
                        ? `https://assets.smartkarma.com/icons/sparkle-single/sparkle-gray.svg`
                        : `https://assets.smartkarma.com/icons/sparkle-single/sparkle-pink.svg`
                    }
                    alt="Sparkle"
                    width={mobile ? 15 : 20}
                    height={mobile ? 14 : 15}
                  />
                  Request Primer
                </div>
              </OverlayTrigger>
            )}
            <div className="hidden md:block">
              <ViewModeToggle
                onToggle={setCompactView}
                isCompact={isCompact}
                showPager
                setSize={setSize}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={twMerge(
          styles.filter,
          "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center md:justify-between"
        )}
      >
        <div className="flex items-center">
          <span
            className={twMerge(styles.refresh, "hidden md:block")}
            onClick={() => mutate()}
          >
            Refresh
          </span>
          <div
            className={twMerge(styles.filterBox, "ml-0 md:ml-[30px] mr-[10px]")}
          >
            <FontAwesomeIcon icon={faSearch} />
            <OverlayTrigger
              placement="bottom"
              overlay={
                filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
              }
            >
              <input
                value={filterTxtValue}
                autoComplete="false"
                autoCapitalize="false"
                onChange={(e) => setFilterTxtValue(e.target.value)}
                aria-label="search"
                placeholder="Search"
              />
            </OverlayTrigger>
          </div>
        </div>
        <div className="flex md:w-auto space-x-[10px]">
          <FocusedSwitch
            checked={focusedOnly}
            onSwitch={setFocusedOnly}
            tooltip={
              <div className="text-center">
                Activate to hide Insights where {name} is not the primary
                subject
              </div>
            }
          />
          <span className={styles.dropdown}>
            <Dropdown>
              <Dropdown.Toggle variant="success" id="dropdown-basic">
                {sortOption.name} <FontAwesomeIcon icon={faCaretDown} />
              </Dropdown.Toggle>

              <Dropdown.Menu className={styles.menu}>
                {sortOptions.map((item) => (
                  <Dropdown.Item
                    className={classNames(
                      styles.item,
                      sortOption.id === item.id && styles.active
                    )}
                    key={item.id}
                    onClick={() => setSortOption(item)}
                  >
                    {item.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </span>
          <div className="hidden md:block">
            <ViewModeToggle
              onToggle={setCompactView}
              isCompact={isCompact}
              showPager
              setSize={setSize}
            />
          </div>
        </div>
      </div>
    );
  }, [
    account?.canRequestAIPrimer,
    filterTxtError,
    filterTxtValue,
    focusedOnly,
    hoverPrimer,
    isCompact,
    isValidating,
    mobile,
    mutate,
    name,
    onRequestPrimer,
    setSize,
    sortOption.id,
    sortOption.name,
    tab,
  ]);

  const list = useMemo(() => {
    return (
      <div className={styles.list}>
        {isRefreshing ? (
          <Loader />
        ) : (
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size, setSize }}
            isReachingEnd={!hasMore}
            endingIndicator={
              isValidating ? (
                <Loader numberOfItems={5} />
              ) : isEmpty ? (
                <EmptyState
                  type={filterTxt ? "search" : "publication"}
                  subType={tab}
                  query={filterTxt}
                  onActionClick={onEmptyStateActionClick}
                  onClearClick={() => setFilterTxtValue("")}
                  entity={entity}
                />
              ) : page ? (
                <Paginator totalPages={totalPages} />
              ) : (
                <div className={styles.endOfList}>No more insights</div>
              )
            }
          >
            <>
              {publications?.map?.((publication: any, index: number) => {
                if (index === 1) {
                  return (
                    <div key={publication.id} className="">
                      <PublicationSnippet
                        key={publication.id}
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        originEntity={entity}
                        origin={origin}
                        swr={swr}
                        style={snippetStyle}
                      />
                      <DianomiAd type="in_list_medium" />
                    </div>
                  );
                }

                if (index === 4) {
                  return (
                    <div key={publication.id} className="">
                      <PublicationSnippet
                        key={publication.id}
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        originEntity={entity}
                        origin={origin}
                        swr={swr}
                        style={snippetStyle}
                      />
                      <DianomiAd type="in_list" />
                    </div>
                  );
                }

                return (
                  <PublicationSnippet
                    key={publication.id}
                    publication={publication}
                    isCompact={isCompact}
                    useModal={authenticated}
                    originEntity={entity}
                    origin={origin}
                    swr={swr}
                    style={snippetStyle}
                  />
                );
              })}
            </>
          </InfiniteScroll>
        )}
      </div>
    );
  }, [
    authenticated,
    data,
    entity,
    filterTxt,
    hasMore,
    isCompact,
    isEmpty,
    isRefreshing,
    isValidating,
    mutate,
    onEmptyStateActionClick,
    origin,
    page,
    publications,
    setSize,
    size,
    snippetStyle,
    swr,
    tab,
    totalPages,
  ]);

  return (
    <div className={styles.container}>
      <Head>
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
      </Head>
      {controller}
      {list}
      {showStripeModal && <AccountUpgradeModal onClose={toggleStripeModal} />}
    </div>
  );
};

export default EntityPageInsightsList;
